import { TabidooTablePage } from '@/components/vyroba/TabidooTablePage';
import { ZaevidovaneFronta } from '@/components/vyroba/ZaevidovaneFronta';
import JsBarcode from 'jsbarcode';
import { useEffect, useRef } from 'react';

type V = Record<string, string>;

/** Čárový kód (Code128) škálovaný na šířku boxu. */
function Barcode({ value }: { value: string }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !value) return;
    el.innerHTML = '';
    try {
      JsBarcode(el, value, {
        format: 'CODE128',
        displayValue: false,
        margin: 0,
        height: 100,
        width: 2,
      });
      // viewBox z rozměrů → CSS width:100% + preserveAspectRatio:none roztáhne na celou šířku.
      const w = Number(el.getAttribute('width')) || el.getBBox().width;
      const h = Number(el.getAttribute('height')) || 100;
      el.setAttribute('viewBox', `0 0 ${w} ${h}`);
      el.removeAttribute('width');
      el.removeAttribute('height');
    } catch {
      /* neplatná hodnota */
    }
  }, [value]);
  return (
    <svg
      ref={ref}
      preserveAspectRatio="none"
      style={{ display: 'block', width: '85%', height: '16mm', margin: '3mm auto' }}
    />
  );
}

/** Boxík s velkou hodnotou nahoře a popiskem dole (3 boxy vpravo). */
function InfoBox({ value, label, bold }: { value?: string; label: string; bold?: boolean }) {
  return (
    <div
      className={`flex flex-col items-center flex-1 ${bold ? 'border-2' : 'border'} border-black`}
    >
      <div className="text-[20px] font-bold py-0.5">{value || ' '}</div>
      <div className="w-full border-t border-black text-[12px] font-bold text-center leading-tight py-0.5 whitespace-pre-line">
        {label}
      </div>
    </div>
  );
}

/** Spodní box s nití: kód + barva nahoře, popisek (Overlock…) dole. */
function NitBox({ kod, barva, label }: { kod?: string; barva?: string; label: string }) {
  return (
    <div className="flex flex-col items-center min-w-0 aspect-square border border-black">
      <div className="flex-1 flex flex-col items-center justify-center min-w-0 px-1 py-0.5">
        <div className="text-[21px] font-bold leading-none">{kod || ' '}</div>
        {barva && (
          <div className="text-[13px] font-semibold leading-tight text-center break-words">
            {barva}
          </div>
        )}
      </div>
      <div className="w-full border-t border-black text-[13px] font-bold italic text-center py-0.5">
        {label}
      </div>
    </div>
  );
}

/**
 * Čáry oddělující skupiny velikostí se stejnými rozměry nápletu — švadlenka
 * podle nich pozná, kde končí jedna „nápletová" skupina. Klíč = ID kategorie
 * (sloupec ID v Databázi kategorií), hodnota = pořadová čísla velikostí, POD
 * kterými se čára kreslí (1 = pod první velikostí). Kategorie, která tu není,
 * se tiskne bez čar.
 */
const SIZE_DIVIDERS: Record<number, number[]> = {
  8: [1, 3, 4], // Mikiny s nápletem - bez panelu
  14: [2, 5, 7], // Noční košile s krátkým rukávem
  16: [2, 5, 7], // Pyžama s dlouhým rukávem
  17: [2, 5, 7], // Pyžama s krátkým rukávem
  28: [2, 5, 7], // Trička s krátkým rukávem
  29: [2, 5, 7], // Trička s dlouhým rukávem
  48: [2, 5, 7], // ROSTOUCÍ pyžama s dlouhým rukávem
  18: [3], // Bermudy - potištěné
  19: [3], // Bermudy - jednobarevné
  20: [3], // ❌ Dívčí kraťasy - potištěné
  40: [3], // Bermudy s gumou - jednobarevné
  41: [3], // Bermudy s gumou - potištěné
  42: [3], // Sukně s gumou - potištěné
  43: [3], // Dívčí kraťasy s gumou - potištěné
  21: [3, 6, 8], // Letní softshellové kalhoty - jednobarevné
  22: [3, 6, 8], // KLASIK Zimní softshellové kalhoty - jednobarevné
  23: [3, 6, 8], // KLASIK Zimní softshellové kalhoty - potištěné
  24: [3, 6, 8], // Letní softshellové kalhoty - potištěné
  26: [3, 6, 8], // Klasické tepláky s dvojitými koleny
  27: [3, 6, 8], // Klasické tepláky bez dvojitých kolen
  32: [3, 6, 8], // SLIM Tepláky bez dvojitých kolen
  33: [3, 6, 8], // SLIM Tepláky s dvojitými koleny
  39: [3, 6, 8], // KLASIK Zimní softshellové kalhoty - REFLEXNÍ
  52: [3, 6, 8], // SLIM Zimní softshellové kalhoty - jednobarevné
  53: [3, 6, 8], // SLIM Zimní softshellové kalhoty - potištěné
  54: [3, 6, 8], // SLIM Zimní softshellové kalhoty - REFLEXNÍ
};

/**
 * Starší zakázky (~39 %) nemají `idKategorieProduktu` — kategorii pak dohledáme
 * podle textu. Obsahuje i názvy, které kategorie nesly dřív (např. „Zimní
 * softshellové kalhoty…" = dnešní KLASIK). Stačí kategorie, které mají čáry.
 */
const CATEGORY_ID_BY_NAME: Record<string, number> = {
  'Mikiny s nápletem - bez panelu': 8,
  'Noční košile s krátkým rukávem': 14,
  'Pyžama s dlouhým rukávem': 16,
  'Pyžama s krátkým rukávem': 17,
  'Bermudy - potištěné': 18,
  'Bermudy - jednobarevné': 19,
  '❌ Dívčí kraťasy - potištěné': 20,
  'Dívčí kraťasy - potištěné': 20, // starší název bez ❌
  'Letní softshellové kalhoty (náplet v pase) - jednobarevné': 21,
  'KLASIK Zimní softshellové kalhoty (náplet v pase) - jednobarevné': 22,
  'Zimní softshellové kalhoty (náplet v pase) - jednobarevné': 22, // starší název bez KLASIK
  'KLASIK Zimní softshellové kalhoty (náplet v pase) - potištěné': 23,
  'Zimní softshellové kalhoty (náplet v pase) - potištěné': 23, // starší název bez KLASIK
  'Letní softshellové kalhoty (náplet v pase) - potištěné': 24,
  'Klasické tepláky s dvojitými koleny a vysokým pasem': 26,
  'Klasické tepláky bez dvojitých kolen a vysokým pasem': 27,
  'Trička s krátkým rukávem': 28,
  'Trička s dlouhým rukávem': 29,
  'SLIM Tepláky bez dvojitých kolen a vysokým pasem': 32,
  'SLIM Tepláky s dvojitými koleny a vysokým pasem': 33,
  'Bermudy s gumou - jednobarevné': 40,
  'Bermudy s gumou - potištěné': 41,
  'Sukně s gumou - potištěné': 42,
  'Dívčí kraťasy s gumou - potištěné': 43,
  'KLASIK Zimní softshellové kalhoty (náplet v pase) - REFLEXNÍ': 39,
  'Zimní softshellové kalhoty (náplet v pase) - REFLEXNÍ': 39, // starší název bez KLASIK
  'SLIM Zimní softshellové kalhoty (náplet v pase) - jednobarevné': 52,
  'SLIM Zimní softshellové kalhoty (náplet v pase) - potištěné': 53,
  'SLIM Zimní softshellové kalhoty (náplet v pase) - REFLEXNÍ': 54,
  'ROSTOUCÍ pyžama s dlouhým rukávem': 48,
};

/** A6 výrobní list (105×148 mm) — 1:1 dle předlohy z Tabidoo, pro tisk na Zebře. */
function A6VyrobaSheet({ v }: { v: V }) {
  // Velikosti: všechny s popiskem (i prázdné množství) — jako v předloze.
  const sizes: { label: string; qty: string }[] = [];
  for (let n = 1; n <= 11; n++) {
    const label = v[`vel${n}_label`] || v[`velikost${n}_label`] || '';
    if (label) sizes.push({ label, qty: v[`vel${n}_value`] || '' });
  }
  // ≤10 velikostí → víc místa, o 1px větší písmo.
  const sizeFont = sizes.length <= 10 ? 'text-[16px]' : 'text-[15px]';

  // Barva zipu se tiskne jen u softshellů (jinde je pole nesmysl / překlep v datech).
  // „-" je default z Tabidoo = nevyplněno, řádek pak vynecháme.
  const barvaZipu = (v.barvaZipu ?? '').trim();
  const zip = /softshell/i.test(v.kategorieProduktu ?? '') && barvaZipu !== '-' ? barvaZipu : '';

  // Pod kterými velikostmi se kreslí oddělovací čára (dle kategorie). Čára pod
  // poslední (a dál) nic neodděluje — zakázka může mít míň velikostí než kategorie.
  const katId =
    Number(v.idKategorieProduktu) || CATEGORY_ID_BY_NAME[(v.kategorieProduktu ?? '').trim()] || 0;
  const dividers = new Set((SIZE_DIVIDERS[katId] ?? []).filter((n) => n < sizes.length));

  return (
    // Po stranách 7 mm (4 mm okraj + 3 mm rezerva) — tiskárny si kus kraje
    // ukrojí a text se pak dotýkal hrany.
    <div
      className="w-full h-full text-black flex flex-col"
      style={{ padding: '4mm 7mm', fontSize: '11px', fontFamily: 'Arial, Helvetica, sans-serif' }}
    >
      {/* Hlavička */}
      <div className="flex justify-between gap-2">
        <div className="min-w-0 text-[14px] font-bold leading-relaxed">
          <div className="flex items-end gap-1">
            <span className="shrink-0">Švadlenka:</span>
            <span className="flex-1 border-b border-black min-w-[35mm]">&nbsp;</span>
          </div>
          <div>ID zakázky: {v.ID_zakazky}</div>
          <div>Kód produktu: {v.kodProduktu}</div>
          <div>100% normy: {v.casovaNarocnostSitiPrepocet}</div>
        </div>
        {/* Fixní logo V-Mart (ne fotka produktu). */}
        <img
          src="/logo-vmart.png"
          alt="V-Mart"
          className="shrink-0 object-contain"
          style={{ width: '20mm', height: '20mm' }}
        />
      </div>
      <div className="text-[17px] font-bold leading-tight mt-1 border-b-2 border-black pb-1">
        {v.nazevProduktu}
      </div>

      {/* Střed: velikosti vlevo, barcode + boxy + materiál vpravo */}
      <div className="flex gap-3 mt-1.5 flex-1 min-h-0">
        {/* Velikosti */}
        <div className="w-[35%] shrink-0 flex flex-col">
          {sizes.map((s, i) => (
            <div
              key={s.label}
              className={`${sizeFont} font-bold py-0.5 whitespace-nowrap ${
                dividers.has(i + 1) ? 'border-b border-black' : ''
              }`}
            >
              {s.label} - {s.qty}
            </div>
          ))}
        </div>

        {/* Pravý sloupec */}
        <div className="flex-1 min-w-0 flex flex-col">
          <Barcode value={v.ID_zakazky || ''} />
          <div className="flex gap-1.5 mt-2">
            <InfoBox value={v.etiketaVMart} label={'Etiketa\nV-Mart'} />
            <InfoBox value={v.cisloCedulek} label="Číslo cedulek" />
            <InfoBox value={v.celkemKs} label={'Celkem\nks'} bold />
          </div>
          <div className="mt-2 text-[14px] leading-relaxed space-y-0.5">
            <div>
              <span className="font-bold">Látka:</span> {v.latka}
            </div>
            <div>
              <span className="font-bold">Náplet:</span> {v.naplet}
            </div>
            <div>
              <span className="font-bold">Číslo látky:</span> {v.cisloLatky}
            </div>
            <div>
              <span className="font-bold">Číslo nápletu:</span> {v.cisloNapletu}
            </div>
            {zip && (
              <div>
                <span className="font-bold">Barva zipu:</span> {zip}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spodní nitě — grid, ať jsou boxy stejně široké i při dlouhém názvu barvy. */}
      <div className="grid grid-cols-4 gap-3 mt-1.5">
        <NitBox kod={v.niteOverlockKod} barva={v.niteOverlockBarva} label="Overlock" />
        <NitBox kod={v.niteKapsyKod} barva={v.niteKapsyBarva} label="Kapsy" />
        <NitBox kod={v.niteDvojjehlaKod} barva={v.niteDvojjehlaBarva} label="Dvojjehla" />
        <NitBox kod={v.niteNapletKod} barva={v.niteNapletBarva} label="Náplet" />
      </div>
    </div>
  );
}

/** VÝROBA — zrcadlo Tabidoo tabulky „vyroba" (hlavní tabulka spojující produkty). */
export function DatabazeVyrobyPage() {
  return (
    <TabidooTablePage
      table="vyroba"
      serverLimit={500}
      storagePrefix="vyroba-tabidoo"
      title="Výroba"
      defaultSort={{ key: 'ID_zakazky', dir: 'desc' }}
      renderTop={({ create, reload }) => (
        <ZaevidovaneFronta
          onCreate={(prefill, onSaved) => create(prefill, onSaved)}
          reload={reload}
        />
      )}
      createLabel="Nová zakázka"
      statusColumn="stav"
      statusColors={{
        'Šije se': 'bg-[#ffedd5] hover:bg-[#fed7aa]',
        Došito: 'bg-[#d1fae5] hover:bg-[#a7f3d0]',
        Hotovo: 'bg-[#86efac] hover:bg-[#4ade80]',
      }}
      statusChips={[
        'Rozpracováno',
        'Připraveno k přidělení',
        'Přiděleno švadlence',
        'Šije se',
        'Došito',
        'Hotovo',
      ]}
      suspiciousBanner={{
        label: 'Podezřelé zakázky (nešije se 7+ dní) → prověřit',
        dateColumn: 'datum', // systémové datum vzniku záznamu
        olderThanDays: 7,
        excludeStatuses: ['Šije se', 'Došito', 'Hotovo'],
      }}
      renderA6Sheet={(v) => <A6VyrobaSheet v={v} />}
      doneBadge={{ nameColumn: 'nazevProduktu', statusValue: 'Hotovo' }}
    />
  );
}
